import React, { createContext, useState } from 'react'
import gql from '../utils/graphql'
import { login } from '../graphql/queries'
import get from 'lodash/get'
import useUser from '../hooks/useUser'
import useLicenses from '../hooks/useLicenses'
import useRequisition from '../hooks/useRequisition'
import useNotifications from '../hooks/useNotifications'
import { REQUISITION_STATUSES, TEST_TYPES } from '../constants'

export const AppContext = createContext()

export default ({ children }) => {
  const [sessionId, updateSessionId] = useState()
  const [shouldUpdateLicenses, setShouldUpdateLicenses] = useState(false)
  const [shouldUpdateNotes, setShouldUpdateNotes] = useState(false)
  const [shouldUpdateConsultations, setShouldUpdateConsultations] = useState(false)

  const [activeRequisition, setActiveRequisition] = useRequisition()
  const [activePatient, setActivePatient] = useState()
  const [patientLastName, setPatientLastName] = useState('')
  const [barcode, setBarcode] = useState('')

  const [user, reloadUser] = useUser(sessionId)

  const [
    requisitionNotifications,
    updateRequisitionNotifications
  ] = useNotifications(
    sessionId,
    get(user, 'oktaId'),
    REQUISITION_STATUSES.CREATED
  )
  const [resultNotifications, updateResultNotifications] = useNotifications(
    sessionId,
    get(user, 'oktaId'),
    REQUISITION_STATUSES.TEST_RESULTS_RECEIVED
  )
  const [
    covidResultNotifications,
    updateCovidResultNotifications
  ] = useNotifications(
    sessionId,
    get(user, 'oktaId'),
    REQUISITION_STATUSES.TEST_RESULTS_RECEIVED,
    TEST_TYPES.COVID_19
  )

  const refreshNotifications = () => {
    updateRequisitionNotifications()
    updateResultNotifications()
    updateCovidResultNotifications()
  }

  const [doctorId, licenses, operatingStates] = useLicenses({
    sessionId,
    oktaId: get(user, 'oktaId'),
    shouldUpdate: shouldUpdateLicenses
  })

  const updateLicenses = () => {
    setShouldUpdateLicenses(!shouldUpdateLicenses)
  }

  const updateNotes = () => {
    setShouldUpdateNotes(!shouldUpdateNotes)
  }

  const updateConsultations = () => {
    setShouldUpdateConsultations(!shouldUpdateConsultations)
  }

  const setSessionId = session => {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem('session', session)
    updateSessionId(session)
  }

  const endSession = () => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('session')
    updateSessionId(null)
  }

  const startSession = async payload => {
    if (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('session')
    ) {
      setSessionId(localStorage.getItem('session'))
    } else if (payload) {
      const session = get(
        await gql({
          operation: login,
          path: 'login',
          variables: {
            username: get(payload, 'username'),
            password: get(payload, 'password')
          }
        }),
        'body.sessionId'
      )
      if (!session) return false
      setSessionId(session)
    } else {
      endSession()
      return false
    }
    reloadUser()
    return true
  }

  const context = {
    sessionId,
    setSessionId,
    startSession,
    endSession,
    user,
    doctorId,
    licenses,
    updateLicenses,
    operatingStates,
    updateNotes,
    updateConsultations,
    shouldUpdateNotes,
    shouldUpdateConsultations,
    activeRequisition,
    setActiveRequisition,
    requisitionNotifications,
    resultNotifications,
    refreshNotifications,
    covidResultNotifications,
    activePatient,
    setActivePatient,
    setPatientLastName,
    patientLastName,
    setBarcode,
    barcode
  }

  return <AppContext.Provider value={context}>{children}</AppContext.Provider>
}
